import { quote, requestMethod } from "../config/";
import config from "../env.config";
import { fetch } from "../shared/services/api.service";
import { checkApiSuccess } from "../utils/index";
import { resetNewProfile } from "./adminUserOnboard";
import { updateCurrUser } from "./auth";

/**
 * handling api for update password
 * @param {credential of the user}
 * @returns {void}
 */

export const updateProfile = async (profileData) => {
  let requestConfig = {
    method: requestMethod.PUT,
    url: `${config.BASE_URL.BASE_API_URL}/user/updateUserProfile`,
    data: profileData,
  };
  let response = await fetch(requestConfig, true, true);
  if (checkApiSuccess(response)) {
    return response.data.data;
  } else {
    return quote.FILE_MUST_BE_AN_IMAGE;
  }
};

/**
 * handling api for get user profile info
 * @param {credential of the user}
 * @returns {void}
 */

export const getUserDetails = (userId) => {
  return async (dispatch) => {
    let requestConfig = {
      method: requestMethod.GET,
      url: `${config.BASE_URL.BASE_API_URL}/user/getUserDetails`,
      params: {
        userId: userId,
      },
    };
    let response = await fetch(requestConfig, false, false);
    if (checkApiSuccess(response)) {
      const userData = response.data.data[0];
      if (userId) {
        const statusData = await dispatch(getUserStatus(userId));
        if (statusData?.userStatus) {
          const availabilityStatus = statusData.userStatus.StatusText;
          const customStatus = statusData.userStatus.customStatus;
          userData.availabilityStatus = availabilityStatus;
          userData.customStatus = customStatus;
        }
      }

      dispatch(updateCurrUser(userData));
      return userData;
    }
  };
};

/**
 * handling api for getting Timezones
 * @returns {array}
 */
export const getTimezones = () => {
  return async (dispatch) => {
    let requestConfig = {
      method: requestMethod.GET,
      url: `${config.BASE_URL.BASE_API_URL}/app/getTimezones`,
    };
    let response = await fetch(requestConfig, false, false);
    if (checkApiSuccess(response)) {
      return response.data.data;
    }
  };
};

export const getTimezoneByCoordinates = (timezone) => {
  return async () => {
    let requestConfig = {
      method: requestMethod.GET,
      url: `${config.BASE_URL.BASE_API_URL}/app/getTimezoneByCoordinates`,
      params: {
        latitude: timezone.latitude,
        longitude: timezone.longitude,
      },
    };
    let response = await fetch(requestConfig, true, false);
    if (checkApiSuccess(response)) {
      return response.data.data;
    }
  };
};

/**
 * handling api for getting user status
 * @param {uId} - User ID to get status for
 * @returns {Object} User status data with statusPresent and userStatus
 * Transforms API response from IsAvailable/StatusText format to availabilityStatus/customStatus format
 */
export const getUserStatus = (uId) => {
  return async (dispatch) => {
    let requestConfig = {
      method: requestMethod.GET,
      url: `${config.BASE_URL.BASE_API_URL}/user/getUserStatus`,
      params: {
        uId: uId,
      },
    };
    let response = await fetch(requestConfig, false, false);
    if (checkApiSuccess(response)) {
      const statusData = response.data.data;
      if (statusData?.userStatus) {
        const { IsAvailable, StatusText } = statusData.userStatus;
        const availabilityStatus = IsAvailable === 1 ? "Available" : "Away";
        const customStatus = StatusText || null;
        return {
          ...statusData,
          userStatus: {
            ...statusData.userStatus,
            StatusText: availabilityStatus,
            availabilityStatus: availabilityStatus,
            customStatus: customStatus,
          },
          timePresets: statusData.timePresets || [],
        };
      }

      return {
        ...statusData,
        timePresets: statusData.timePresets || [],
      };
    }
    return null;
  };
};

/**
 * handling api for updating user availability status
 * @param {userId, status, clearAwayAt, customStatus, scheduledFrom, scheduledTo} - userId, status (Available/Away), optional clearAwayAt date, custom status message, and scheduled availability times
 * @returns {void}
 * Supports custom status messages and scheduled availability
 * @author Muskan Thakur
 */
export const updateUserAvailabilityStatus = (
  userId,
  status,
  clearAwayAt = null,
  customStatus = null,
  scheduledFrom = null,
  scheduledTo = null,
  timePresetId = null
) => {
  return async (dispatch, getState) => {
    const currentUser = getState().auth.user;
    const isCurrentUser =
      currentUser?.id && (userId === currentUser.id || userId === currentUser.id.toString() || Number(userId) === Number(currentUser.id));
    const isAvailable = status === "Available" ? 1 : 0;
    const statusText = customStatus || null;

    /* Convert dates to UTC ISO string format
     */
    const expiresAtUTC = clearAwayAt ? new Date(clearAwayAt).toISOString() : null;
    const startsAtUTC = scheduledFrom ? new Date(scheduledFrom).toISOString() : null;

    const requestBody = {
      uId: userId,
      isAvailable: isAvailable,
      statusText: statusText,
      statusEmoji: null,
      expiresAt: expiresAtUTC,
      startsAt: startsAtUTC,
      timePresetId: timePresetId,
    };



    let requestConfig = {
      method: requestMethod.POST,
      url: `${config.BASE_URL.BASE_API_URL}/user/updateUserStatus`,
      data: requestBody,
    };

    let response = await fetch(requestConfig, true, false);
    if (checkApiSuccess(response)) {
      if (isCurrentUser) {
        dispatch(
          updateCurrUser({
            availabilityStatus: status,
            clearAwayAt: clearAwayAt,
            customStatus: customStatus,
            scheduledFrom: scheduledFrom,
            scheduledTo: scheduledTo,
          })
        );
      }

      return { success: true, data: response.data.data };
    } else {
      return { success: false, error: response.data?.message };
    }
  };
};
